import { useNavigate } from "react-router-dom";

import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult
} from "@tanstack/react-query";

import { UseGetAttributeValuesParams } from "@/components/dataset/datasetAttributeFilter/types";
import { Timezone } from "@/constants/dateTime";
import { MESSAGE } from "@/constants/message";
import {
  getDeepestAdminFilterFirst,
  isAdminAreaLevel,
  mapDatasetFiltersToPayload
} from "@/helpers/datasetAttributeHelper";
import { removeStartFromUnderscore } from "@/helpers/string";
import useToast, { EToastType } from "@/hooks/ui/useToast";
import {
  AddUpdateRowsDatasetPayload,
  AddUpdateRowsDatasetResponse,
  AttributeOption,
  DataProviderItem,
  DatasetAnalysisPayload,
  DatasetAttributeSchema,
  DatasetAttributeValue,
  DatasetData,
  DatasetDetailResponse,
  DatasetExploreItem,
  DatasetFilter,
  DatasetFilterPayload,
  DatasetMetadata,
  DatasetMetaDataPayload,
  DatasetMetadataQueryParams,
  DatasetSchemaPayload,
  DatasetSchemas,
  DeleteRowsDatasetResponse,
  EAttributeType,
  ExportDatasetFileType,
  FilterOption,
  GetDatasetListPayload,
  GetDatasetListResponse,
  H3Configuration,
  OperationStatus,
  SpatialAggregationTypeItem,
  TranslateDatasetGeometryErrorResponse,
  TranslateDatasetGeometryPayload,
  TranslateDatasetGeometryResponse
} from "@/types/dataset";
import { CustomError } from "@/utils/errors";
import { generateRawDatasetDataOnly } from "@/utils/generateDatasetData";

import {
  addImportDataset,
  addRowsDataset,
  datasetRemoveNulls,
  deleteDataset,
  deleteRowsDataset,
  exportDataset,
  getAttributeValuesByAttributeId,
  getDataProviders,
  getDatasetByIdSchema,
  getDatasetDetail,
  getDatasetList,
  getDatasetMetadata,
  getDatasetParsingStatus,
  getOperationStatus,
  getSpatialAggregationTypes,
  importDataset,
  patchDatasetMetadata,
  patchDatasetSchema,
  postDatasetAnalysis,
  replaceImportDataset,
  streamDatasetRawData,
  translateDatasetGeometry,
  updateRowDataset,
  uploadDataset
} from "./datasetApi";

const removeFilterForAttribute = (
  filters: DatasetFilter[],
  attr: AttributeOption
) => {
  return filters.filter((item) => {
    // Primary match: filterId matches the attribute id
    const idMatch = item.filterId === attr?.id;

    // Secondary match: attributeCode matches (if both have values)
    // If attribute_code is empty/undefined on either side, only use idMatch
    const codeMatch =
      item.attributeCode && attr?.attribute_code
        ? item.attributeCode === attr.attribute_code
        : true;

    // Remove the filter if both id and code match
    return !(idMatch && codeMatch);
  });
};

export const useUploadDatasetMutation = (
  options?: UseMutationOptions<
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    any,
    Error,
    FormData
  >
) => {
  return useMutation({
    mutationFn: (file: FormData) => uploadDataset(file),
    ...options
  });
};

export const useImportDatasetMutation = (
  options?: UseMutationOptions<
    { operation_id: string },
    Error,
    { file: FormData; signal?: AbortSignal; isShapefileUploadEnabled?: boolean }
  >
) => {
  return useMutation({
    mutationFn: ({
      file,
      signal,
      isShapefileUploadEnabled
    }: {
      file: FormData;
      signal?: AbortSignal;
      isShapefileUploadEnabled?: boolean;
    }) => importDataset(file, signal, isShapefileUploadEnabled),
    ...options
  });
};

export const useAddImportDatasetMutation = (
  options?: UseMutationOptions<
    { operation_id: string },
    Error,
    {
      file: FormData;
      signal?: AbortSignal;
      id: string;
      isShapefileUploadEnabled?: boolean;
    }
  >
) => {
  return useMutation({
    mutationFn: ({
      file,
      signal,
      id,
      isShapefileUploadEnabled
    }: {
      file: FormData;
      signal?: AbortSignal;
      id: string;
      isShapefileUploadEnabled?: boolean;
    }) => addImportDataset(id, file, signal, isShapefileUploadEnabled),
    ...options
  });
};

export const useReplaceImportDatasetMutation = (
  options?: UseMutationOptions<
    { operation_id: string },
    Error,
    {
      file: FormData;
      signal?: AbortSignal;
      id: string;
      isShapefileUploadEnabled?: boolean;
    }
  >
) => {
  return useMutation({
    mutationFn: ({
      file,
      signal,
      id,
      isShapefileUploadEnabled
    }: {
      file: FormData;
      signal?: AbortSignal;
      id: string;
      isShapefileUploadEnabled?: boolean;
    }) => replaceImportDataset(id, file, signal, isShapefileUploadEnabled),
    ...options
  });
};

export const useExportDatasetMutation = (
  options?: UseMutationOptions<
    { operation_id: string },
    { message: CustomError },
    { id: string; fileType: ExportDatasetFileType }
  >
) => {
  return useMutation({
    mutationFn: ({ id, fileType }) => exportDataset(id, fileType),
    ...options
  });
};

export const useGetOperationStatus = (
  operationId: string,
  options?: Omit<
    UseQueryOptions<OperationStatus, Error, OperationStatus>,
    "queryKey" | "queryFn"
  >
) => {
  return useQuery({
    queryKey: ["operation-status", operationId],
    queryFn: () => getOperationStatus(operationId),
    ...options
  });
};

export const useGetDatasetParsingStatus = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  options?: UseQueryOptions<any, Error, any>
) => {
  return useQuery({
    queryKey: ["dataset-parsing-status"],
    queryFn: () => getDatasetParsingStatus(),
    ...options
  });
};

export const useGetDatasetListQuery = (
  payload: GetDatasetListPayload,
  options?: Omit<
    UseQueryOptions<GetDatasetListResponse, Error, GetDatasetListResponse>,
    "queryKey" | "queryFn"
  >
) => {
  return useQuery({
    queryKey: ["datasets-list", payload],
    queryFn: () => getDatasetList(payload),
    ...options
  });
};

export const useGetDatasetData = (
  { id, limit }: { id: string; limit?: number },
  options?: Omit<
    UseQueryOptions<DatasetData, Error, DatasetData>,
    "queryKey" | "queryFn"
  >
) => {
  return useQuery({
    queryKey: ["dataset-data", id, limit],
    queryFn: () =>
      streamDatasetRawData({
        datasetId: id,
        limit,
        generateDatasetData: generateRawDatasetDataOnly
      }),
    enabled: options?.enabled,
    ...options
  });
};

type GetDatasetDataParams = {
  id: string;
  filter?: DatasetFilter[];
  h3Configuration?: H3Configuration | null;
  show_id?: boolean;
};

/**
 * Hook to get dataset data using the POST method
 */
export const useGetDatasetDataMutation = ({
  timezone,
  options
}: {
  timezone?: Timezone;
  options?: UseMutationOptions<
    DatasetData,
    Error,
    {
      id: string;
      filter?: DatasetFilter[];
      h3Configuration?: H3Configuration | null;
      show_id?: boolean;
    }
  >;
} = {}) => {
  return useMutation({
    mutationFn: ({
      id,
      filter,
      h3Configuration,
      show_id
    }: GetDatasetDataParams) => {
      const filterPayload: DatasetFilterPayload[] = filter?.length
        ? mapDatasetFiltersToPayload(
            getDeepestAdminFilterFirst(filter),
            timezone
          )
        : [];

      return streamDatasetRawData({
        datasetId: id,
        filterPayload,
        h3Configuration,
        show_id
      });
    },
    ...options
  });
};

export const usePostDatasetAnalysis = (
  options?: UseMutationOptions<
    { id: string; message?: string },
    CustomError,
    DatasetAnalysisPayload
  >
) => {
  return useMutation({
    mutationFn: (payload: DatasetAnalysisPayload) =>
      postDatasetAnalysis(payload),
    ...options
  });
};

export const useMutationDatasetMetadata = (
  options?: UseMutationOptions<
    DatasetMetadata,
    Error,
    DatasetMetadataQueryParams
  >
): UseMutationResult<DatasetMetadata, Error, DatasetMetadataQueryParams> => {
  return useMutation({
    mutationFn: ({ id, h3Configuration, show_id, group_by }) =>
      getDatasetMetadata(id, h3Configuration, show_id, group_by),
    ...options
  });
};

export const useMutationPatchDatasetSchema = (
  options?: UseMutationOptions<
    DatasetSchemaPayload,
    Error,
    DatasetSchemaPayload,
    { previousMetadata: DatasetMetadata | undefined }
  >
): UseMutationResult<
  DatasetSchemaPayload,
  Error,
  DatasetSchemaPayload,
  { previousMetadata: DatasetMetadata | undefined }
> => {
  return useMutation({
    mutationFn: (payload: DatasetSchemaPayload) => patchDatasetSchema(payload),
    ...options
  });
};

export default function useBatchGetDatasetMetadata(
  datasetIds: string[],
  options?: Omit<
    UseQueryOptions<DatasetMetadata[], Error, DatasetAttributeSchema[]>,
    "queryKey" | "queryFn"
  >
) {
  return useQuery({
    queryKey: ["dataset-metadata-batch", datasetIds],
    queryFn: async () => {
      if (!datasetIds || datasetIds.length === 0) {
        return [];
      }
      const promises = datasetIds.map((id) => getDatasetMetadata(id));
      return Promise.all(promises);
    },
    enabled: !!datasetIds && datasetIds.length > 0,
    ...options
  });
}

export const useGetDatasetMetadata = (
  id: string,
  options?: UseQueryOptions<DatasetMetadata, Error, DatasetMetadata>
): UseQueryResult<DatasetMetadata, Error> => {
  return useQuery({
    queryKey: ["dataset-metadata", id],
    queryFn: () => getDatasetMetadata(id),
    ...options
  });
};

export const useGetDatasetMetadataQuery = (
  dataset: DatasetExploreItem | null,
  options?: Omit<
    UseQueryOptions<DatasetDetailResponse, Error, DatasetDetailResponse>,
    "queryKey" | "queryFn"
  >
) => {
  return useQuery({
    queryKey: ["dataset-metadata", ...(dataset ? Object.values(dataset) : [])],
    queryFn: () => getDatasetDetail(removeStartFromUnderscore(dataset?.id)),
    enabled: !!dataset?.id,
    ...options
  });
};

export const useMutationPatchDatasetMetadata = (
  options?: UseMutationOptions<
    DatasetMetaDataPayload,
    { message: CustomError },
    DatasetMetaDataPayload
  >
): UseMutationResult<
  DatasetMetaDataPayload,
  { message: CustomError },
  DatasetMetaDataPayload
> => {
  const queryClient = useQueryClient();
  const { onSuccess: onSuccessCallback, ...restOptions } = options || {};

  return useMutation({
    mutationFn: (payload: DatasetMetaDataPayload) =>
      patchDatasetMetadata(payload),
    onSuccess: (data, variables, onMutateResult, context) => {
      // Need to invalidate datasets-list query after patching metadata
      queryClient.invalidateQueries({ queryKey: ["datasets-list"] });

      // Then run the onSuccess callback if it exists
      onSuccessCallback?.(data, variables, onMutateResult, context);
    },
    ...restOptions
  });
};

export const useMutationDeleteDataset = (
  options?: UseMutationOptions<void, Error, string>
): UseMutationResult<void, Error, string> => {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteDataset(id),
    onSuccess: () => {
      // Need to invalidate datasets-list after deleting the dataset
      queryClient.invalidateQueries({ queryKey: ["datasets-list"] });
      showToast({
        type: EToastType.SUCCESS,
        autoClose: 3000,
        message: (
          <div>
            <h1 className="text-txtHeading font-bold">
              {MESSAGE.DATASET_DELETE_SUCCESS}
            </h1>
            <span className="text-sm text-txtHeading">
              We’ve deleted the dataset as requested.
            </span>
          </div>
        )
      });
      navigate("/dataset-management");
    },
    onError: () => {
      showToast({
        type: EToastType.ERROR,
        message: MESSAGE.DELETE_DATASET_FAILED
      });
    },
    retry: 0,
    ...options
  });
};

export const useGetAttributeValues = (
  params: UseGetAttributeValuesParams,
  options?: UseQueryOptions<FilterOption[], Error, FilterOption[]>
): UseQueryResult<FilterOption[], Error> => {
  const {
    datasetId,
    selectedFilter: attribute,
    selectedEditFilter: editAttribute,
    isEditMode,
    savedFilters,
    isMultipleSelection,
    timezone,
    showAnalysisResultPopup,
    latestAnalysisId,
    editFilterIndex
  } = params;

  const attributeToFetch = attribute?.paired_key_attribute_id
    ? {
        ...attribute,
        id: attribute.paired_key_attribute_id,
        value: attribute.paired_key_attribute_id
      }
    : attribute;

  return useQuery({
    queryKey: [
      "dataset-attribute-value",
      datasetId,
      attributeToFetch,
      editAttribute,
      isEditMode,
      savedFilters,
      editFilterIndex
    ],
    queryFn: async () => {
      let filters: DatasetFilter[] = savedFilters || [];

      if (attributeToFetch?.data_type === EAttributeType.BOOLEAN) {
        return [
          { id: "1", label: "True", value: "true" },
          { id: "2", label: "False", value: "false" }
        ];
      }

      // When editing, apply index-based filtering:
      // - Admin area filters are ALWAYS included (they're the base selection)
      // - For non-admin filters: only include filters that come BEFORE the edit index
      // Example: editing at index 2 → include adm_area_level_n + filters at index 0, 1
      if (isEditMode && editFilterIndex !== undefined && editFilterIndex >= 0) {
        filters = filters.filter((f, idx) => {
          // Always include admin area filters
          if (isAdminAreaLevel(f.attributeCode)) return true;
          // Only include non-admin filters that come BEFORE the edit index
          return idx < editFilterIndex;
        });
      } else {
        // If not in edit mode or no editFilterIndex provided, remove only the current filter
        if (editAttribute)
          filters = removeFilterForAttribute(filters, editAttribute);
        if (attribute) filters = removeFilterForAttribute(filters, attribute);
      }

      const filterMap: DatasetFilterPayload[] = mapDatasetFiltersToPayload(
        filters,
        timezone
      );

      const response: DatasetAttributeValue[] =
        await getAttributeValuesByAttributeId({
          datasetId,
          attribute: attributeToFetch,
          filters: filterMap,
          showAnalysisResultPopup,
          latestAnalysisId
        });
      return response?.map((item, index) => ({
        id: `${item.value}-${index}`,
        label: item.display_name,
        value: item.value
      }));
    },
    enabled: !!datasetId && !!attributeToFetch && isMultipleSelection,
    staleTime: 0,
    ...options
  });
};

export type UseGetMissingAttributeValuesParams = {
  datasetId?: string;
  selectedFilter: AttributeOption | null;
  invalidOptions: string[];
  showAnalysisResultPopup?: boolean;
  latestAnalysisId?: string;
};

/**
 * Hook to fetch attribute values for missing/invalid options without applying other filters.
 * This is used when selectedOptions contain values that are not returned by the filtered query.
 * By fetching without filters, we can get the display names for options that were filtered out.
 */
export const useGetMissingAttributeValues = (
  params: UseGetMissingAttributeValuesParams,
  options?: UseQueryOptions<FilterOption[], Error, FilterOption[]>
): UseQueryResult<FilterOption[], Error> => {
  const {
    datasetId,
    selectedFilter: attribute,
    invalidOptions,
    showAnalysisResultPopup,
    latestAnalysisId
  } = params;

  const attributeToFetch = attribute?.paired_key_attribute_id
    ? {
        ...attribute,
        id: attribute.paired_key_attribute_id,
        value: attribute.paired_key_attribute_id
      }
    : attribute;

  return useQuery({
    queryKey: [
      "dataset-attribute-value-missing",
      datasetId,
      attributeToFetch,
      invalidOptions
    ],
    queryFn: async () => {
      if (attributeToFetch?.data_type === EAttributeType.BOOLEAN) {
        return [
          { id: "1", label: "True", value: "true" },
          { id: "2", label: "False", value: "false" }
        ];
      }
      if (!attributeToFetch?.id) return [];
      // Fetch attribute values without filters to get the display names for invalid options
      const response: DatasetAttributeValue[] =
        await getAttributeValuesByAttributeId({
          datasetId,
          attribute: attributeToFetch,
          filters: [{ id: attributeToFetch.id, filter: invalidOptions }], // Pass invalid options as filters to fetch only those values
          showAnalysisResultPopup,
          latestAnalysisId
        });

      // Filter to only return the invalid options we're looking for
      const allValues = response?.map((item, index) => ({
        id: `${item.value}-${index}`,
        label: item.display_name,
        value: item.value
      }));

      // Return only the values that match the invalid options
      return (
        allValues?.filter((item) => invalidOptions.includes(item.value)) || []
      );
    },
    enabled: !!datasetId && !!attributeToFetch && invalidOptions.length > 0,
    staleTime: 0,
    ...options
  });
};

export const useTranslateDatasetGeometry = (
  id: string,
  options?: UseMutationOptions<
    TranslateDatasetGeometryResponse,
    { message: CustomError<TranslateDatasetGeometryErrorResponse> },
    TranslateDatasetGeometryPayload
  >
) => {
  return useMutation({
    mutationFn: (payload: TranslateDatasetGeometryPayload) =>
      translateDatasetGeometry(id, payload),
    ...options
  });
};

export const useDatasetRemoveNulls = (
  id: string,
  options?: UseMutationOptions<
    TranslateDatasetGeometryResponse,
    Error,
    { attribute_id: string }
  >
) => {
  return useMutation({
    mutationFn: (payload: { attribute_id: string }) =>
      datasetRemoveNulls(id, payload),
    ...options
  });
};

export const useGetDatasetByIdSchema = (
  { datasetId, geometryType }: { datasetId?: string; geometryType: string },
  options?: UseQueryOptions<DatasetSchemaPayload, Error, DatasetSchemas>
): UseQueryResult<DatasetSchemas, Error> => {
  return useQuery({
    queryKey: ["dataset", datasetId, "schema", geometryType], // Include geometryType in key
    queryFn: () => getDatasetByIdSchema(datasetId, geometryType),
    enabled: !!datasetId,
    ...options
  });
};

export const useGetSpatialAggregationTypes = (
  options?: Omit<
    UseQueryOptions<
      SpatialAggregationTypeItem[],
      Error,
      SpatialAggregationTypeItem[]
    >,
    "queryKey" | "queryFn"
  >
) => {
  return useQuery({
    queryKey: ["spatial-aggregation-types"],
    queryFn: () => getSpatialAggregationTypes(),
    ...options
  });
};

export const useGetDataProviders = (
  options?: Omit<
    UseQueryOptions<DataProviderItem[], Error, DataProviderItem[]>,
    "queryKey" | "queryFn"
  >
) => {
  return useQuery({
    queryKey: ["data-providers"],
    queryFn: () => getDataProviders(),
    ...options
  });
};

export const useDeleteRowsDataset = (
  datasetId: string,
  options?: UseMutationOptions<DeleteRowsDatasetResponse, Error, string[]>
) =>
  useMutation<DeleteRowsDatasetResponse, Error, string[]>({
    mutationFn: (referenceIds: string[]) =>
      deleteRowsDataset(datasetId, referenceIds),
    ...options
  });

export const useAddRowsDataset = (
  datasetId: string,
  options?: UseMutationOptions<
    AddUpdateRowsDatasetResponse,
    Error,
    {
      data: AddUpdateRowsDatasetPayload[];
      adm_area_source: string | null;
    }
  >
) =>
  useMutation<
    AddUpdateRowsDatasetResponse,
    Error,
    {
      data: AddUpdateRowsDatasetPayload[];
      adm_area_source: string | null;
    }
  >({
    mutationFn: ({
      data,
      adm_area_source
    }: {
      data: AddUpdateRowsDatasetPayload[];
      adm_area_source: string | null;
    }) => addRowsDataset(datasetId, data, adm_area_source),
    ...options
  });

export const useUpdateRowsDataset = (
  datasetId: string,
  options?: UseMutationOptions<
    AddUpdateRowsDatasetResponse,
    Error,
    {
      rowId: string;
      data: AddUpdateRowsDatasetPayload[];
      adm_area_source: string | null;
    }
  >
) =>
  useMutation<
    AddUpdateRowsDatasetResponse,
    Error,
    {
      rowId: string;
      data: AddUpdateRowsDatasetPayload[];
      adm_area_source: string | null;
    }
  >({
    mutationFn: ({
      rowId,
      data,
      adm_area_source
    }: {
      rowId: string;
      data: AddUpdateRowsDatasetPayload[];
      adm_area_source: string | null;
    }) => updateRowDataset(datasetId, rowId, data, adm_area_source),
    ...options
  });
